import { InjectionToken } from '@angular/core';
import { MapAnchorPoint } from '@angular/google-maps';
import { Cluster, ClusterStats, Renderer } from '@googlemaps/markerclusterer';
import { FeatureCollection } from 'geojson';

export interface LatLngLiteral extends google.maps.LatLngLiteral {}
export interface BoundsLiteral extends google.maps.LatLngBoundsLiteral {}

export const POLYGON_COLORS = [
  '#d32f2f',
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#0097a7',
  '#c2185b',
  '#5d4037',
  '#455a64',
  '#424242',
];

export const DEFAULT_COORDS = {
  lat: 43.6611069,
  lng: -116.339548,
} as const;

/**
 * Adapted from {@link https://github.com/ng-maps/ng-maps/blob/main/libs/google/src/lib/options.ts}
 */

export declare enum GoogleMapsScriptProtocol {
  HTTP = 1,
  HTTPS = 2,
  AUTO = 3,
}

export interface GoogleModuleOptions {
  /**
   * The Google Maps API Key (see: https://developers.google.com/maps/documentation/javascript/get-api-key)
   */
  apiKey?: string;
  /**
   * The Google Maps client ID (for premium plans).
   * When you have a Google Maps APIs Premium Plan license, you must authenticate
   * your application with either an API key or a client ID.
   * The Google Maps API will fail to load if both a client ID and an API key are included.
   */
  clientId?: string;
  /**
   * The Google Maps channel name (for premium plans).
   * A channel parameter is an optional parameter that allows you to track usage under your client
   * ID by assigning a distinct channel to each of your applications.
   */
  channel?: string;
  /**
   * Google Maps API version.
   */
  apiVersion?: string;
  /**
   * Host and Path used for the `<script>` tag.
   */
  hostAndPath?: string;
  /**
   * Protocol used for the `<script>` tag.
   */
  protocol?: GoogleMapsScriptProtocol;
  /**
   * Defines which Google Maps libraries should get loaded.
   * For available libraries see: https://developers.google.com/maps/documentation/javascript/libraries
   */
  libraries?: Array<string>;
  /**
   * The default bias for the map behavior is US.
   * If you wish to alter your application to serve different map tiles or bias the
   * application, you can overwrite the default behavior (US) by defining a `region`.
   * See https://developers.google.com/maps/documentation/javascript/basics#Region
   */
  region?: string;
  /**
   * The Google Maps API uses the browser's preferred language when displaying
   * textual information. If you wish to overwrite this behavior and force the API
   * to use a given language, you can use this setting.
   * See https://developers.google.com/maps/documentation/javascript/basics#Language
   */
  language?: string;
}

/**
 * Token for the config of google maps module
 * Please provide an object of type {@link GoogleModuleOptions}.
 */
export const GOOGLE_MAPS_API_CONFIG = new InjectionToken<GoogleModuleOptions | Promise<GoogleModuleOptions>>(
  'GOOGLE_MAPS_API_CONFIG',
);

export type LayerTypes = 'TrafficLayer' | 'TransitLayer' | 'BicyclingLayer';

export interface MapOptions {
  draggable?: boolean;
  scrollwheel?: boolean;
  scaleControl?: boolean;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  zoom?: number;
  disableDoubleClickZoom?: boolean;
  [option: string]: any;
}

export function isZeroes(lat?: number, lng?: number): boolean {
  return (lat ?? 0) === 0 && (lng ?? 0) === 0;
}

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  title: string;
  subtitle: string;
}

/**
 * Convert a latitude and longitude to a MapAnchorPoint
 * @param lat - The latitude of the point
 * @param lng - The longitude of the point
 * @returns A MapAnchorPoint object
 */
export function convertToMapAnchorPoint(lat: number, lng: number): MapAnchorPoint {
  const advancedElement = new google.maps.marker.AdvancedMarkerElement({
    position: new google.maps.LatLng(lat, lng),
  });

  return { getAnchor: () => advancedElement };
}

/**
 * Apply polygons from a GeoJSON feature collection to a Google Map
 * @param {FeatureCollection} featureCollection - GeoJSON feature collection containing polygon features
 * @param {google.maps.Map} map - Google Maps instance to draw on
 * @param {Object} [options] - Optional styling for the polygons
 * @returns {Array} - Array of created polygon objects
 */
export function addFeatureCollectionToMap(
  featureCollection: FeatureCollection,
  map: google.maps.Map,
  options: google.maps.PolygonOptions = {},
) {
  const defaultOptions: google.maps.PolygonOptions = {
    strokeColor: '#FF0000',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#FF0000',
    fillOpacity: 0.15,
  };

  const polygonOptions = { ...defaultOptions, ...options };

  const dataLayer = new google.maps.Data({
    map,
    style: {
      fillColor: polygonOptions.fillColor,
      fillOpacity: polygonOptions.fillOpacity,
      strokeColor: polygonOptions.strokeColor,
      strokeOpacity: polygonOptions.strokeOpacity,
      strokeWeight: polygonOptions.strokeWeight,
    },
  });

  dataLayer.addGeoJson(featureCollection);

  return dataLayer;
}

export function centerAndFitBounds(
  map: google.maps.Map,
  bounds: google.maps.LatLngBounds,
  markers: { lat: number; lng: number }[],
  polygons?: google.maps.Data[],
  options: {
    zoom?: number;
    minimumBounds?: number;
  } = { zoom: 12, minimumBounds: 0.01 },
) {
  if (bounds.isEmpty()) {
    return;
  }

  // If we only have one marker and no areas, center on it with a reasonable zoom
  if (markers?.length === 1 && !polygons?.length) {
    const marker = markers[0];
    map.setCenter(new google.maps.LatLng(marker.lat, marker.lng));
    map.setZoom(options.zoom);
  } else {
    // For multiple markers or areas, use fitBounds with minimum bounds size
    const latDiff = bounds.getNorthEast().lat() - bounds.getSouthWest().lat();
    const lngDiff = bounds.getNorthEast().lng() - bounds.getSouthWest().lng();

    // Ensure minimum bounds size to prevent excessive zoom
    // 0.01 is ~1km at the equator
    if (latDiff < options.minimumBounds || lngDiff < options.minimumBounds) {
      const center = bounds.getCenter();
      const padding = options.minimumBounds / 2; // Half of 0.01 to center the padding
      bounds.extend(new google.maps.LatLng(center.lat() + padding, center.lng() + padding));
      bounds.extend(new google.maps.LatLng(center.lat() - padding, center.lng() - padding));
    }

    map.fitBounds(bounds);
  }
}

/**
 * Brand Heatmap Circular Cluster Renderer
 * Uses your brand colors for a heatmap-style progression
 */
export class BrandHeatmapClusterRenderer implements Renderer {
  private readonly brandColors = {
    red: 'var(--as-red, #E92645)',
    orange: 'var(--as-orange, #FF9824)',
    blue: 'var(--as-blue, #1C69D8)',
  };

  render(cluster: Cluster, _stats: ClusterStats, _map: google.maps.Map): google.maps.marker.AdvancedMarkerElement {
    const count = cluster.markers.length;
    const size = this.getClusterSize(count);
    const { bgColor } = this.getHeatmapColors(count);

    // Create cluster icon
    const svg = `
       <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
         <!-- Simple solid circle -->
         <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" 
                 fill="${bgColor}" 
                 stroke="rgba(255,255,255,0.9)" 
                 stroke-width="2"/>
         <!-- Clean white text -->
         <text x="${size / 2}" y="${size / 2}" 
               text-anchor="middle" 
               dominant-baseline="middle" 
               font-family="system-ui, -apple-system, sans-serif" 
               font-size="${this.getFontSize(size)}" 
               font-weight="600" 
               fill="white">
           ${count}
         </text>
       </svg>
     `;

    const clusterDiv = document.createElement('div');
    clusterDiv.innerHTML = svg;
    clusterDiv.style.cursor = 'pointer';
    clusterDiv.style.filter = 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))';

    return new google.maps.marker.AdvancedMarkerElement({
      position: cluster.position,
      content: clusterDiv,
      zIndex: 1000 + count,
    });
  }

  private getClusterSize(count: number): number {
    if (count < 10) return 35;
    if (count < 50) return 45;
    if (count < 100) return 55;
    return 65;
  }

  private getFontSize(size: number): number {
    return Math.max(10, Math.floor(size * 0.25));
  }

  private getHeatmapColors(count: number): { bgColor: string } {
    if (count < 15) {
      return { bgColor: this.brandColors.blue };
    } else if (count < 50) {
      return { bgColor: this.brandColors.orange };
    } else {
      return { bgColor: this.brandColors.red };
    }
  }
}
